"use client";
import React from "react";
import Link from "next/link";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { usePagination } from "@hooks/usePagination";
import { useCookies } from "next-client-cookies";
import { APPLICATIONS_PER_PAGE } from "@lib/constants";
import { cn } from "@lib/utils";

interface TotalRecordsProps {
  totalRecords: string;
  searchParams: any;
  limitPerPage?: string;
  showTotalResults?: boolean;
  showPageNumbers?: boolean;
}

const Pagination = ({
  totalRecords,
  searchParams,
  limitPerPage,
  showTotalResults = true,
  showPageNumbers = true,
}: TotalRecordsProps) => {
  const currentPage = parseInt(searchParams?.page) || 1;
  const limit =
    limitPerPage ?? (process.env.NEXT_PUBLIC_PAGINATION_LIMIT as string);
  const cookies = useCookies();
  const isArabic = cookies.get("lang") === "ar";
  const totalPages = Math.ceil(parseInt(totalRecords) / APPLICATIONS_PER_PAGE);

  const paginationRange: any = usePagination({
    currentPage,
    totalCount: parseInt(totalRecords),
    siblingCount: 1,
    pageSize: APPLICATIONS_PER_PAGE,
  });

  const getPageQueryString = (page: number) => {
    const queryParams = [];
    queryParams.push(`page=${page}`);
    for (const [param, value] of Object.entries(searchParams)) {
      if (param === "page") continue;
      if (Array.isArray(value)) {
        value.forEach((p) => {
          queryParams.push(`${param}=${p}`);
        });
      } else {
        queryParams.push(`${param}=${value}`);
      }
    }
    return queryParams.join("&");
  };

  const calculateStartIndex = (): number => {
    return currentPage ? (currentPage - 1) * parseInt(limit) + 1 : 1;
  };

  const calculateEndIndex = () => {
    const end = currentPage * parseInt(limit);
    return end > parseInt(totalRecords) ? parseInt(totalRecords) : end;
  };

  return (
    <div className="flex mt-5 flex-col items-center justify-between sm:flex-row border border-[#E7E7E7] rounded-b-[12px] md:h-16 py-4 md:py-0 px-8 md:px-10">
      {showTotalResults && (
        <p className="mb-3 font-medium text-sm sm:text-[15px] leading-7 text-[#767879] sm:mb-0">
          Results:{" "}
          <span className="text-secondary font-bold">
            {" "}
            {calculateStartIndex()} - {calculateEndIndex()}
          </span>{" "}
          / {totalRecords}
        </p>
      )}

      <div className="flex justify-center gap-3">
        <ul className="flex items-center gap-1 sm:gap-2">
          <li className="inline-flex items-center mr-1 sm:mr-2">
            <Link
              href={`?${getPageQueryString(currentPage - 1)}`}
              className={cn(
                "w-7 h-7 flex items-center justify-center text-[#444748]",
                currentPage === 1 ? "pointer-events-none text-[#DADADA]" : ""
              )}
            >
              {isArabic ? (
                <ChevronRightIcon className="w-5 h-5" />
              ) : (
                <ChevronLeftIcon className="w-5 h-5" />
              )}
            </Link>
          </li>

          {showPageNumbers &&
            paginationRange.map((pageNumber: any, i: number) => {
              if (pageNumber === "DOTS") {
                return (
                  <li key={i} className="w-7 h-7">
                    ...
                  </li>
                );
              }

              return (
                <li key={i}>
                  <Link
                    href={`?${getPageQueryString(pageNumber)}`}
                    className={cn(
                      "text-sm sm:text-[15px] font-bold inline-flex items-center justify-center w-7 h-7",
                      currentPage === pageNumber
                        ? "text-secondary"
                        : "text-[#8F9091] hover:text-secondary"
                    )}
                  >
                    <span>{pageNumber}</span>
                  </Link>
                </li>
              );
            })}

          <Link
            href={`?${getPageQueryString(currentPage + 1)}`}
            className={cn(
              "w-7 h-7 flex items-center justify-center text-[#444748]",
              currentPage >= totalPages
                ? "pointer-events-none text-[#DADADA]"
                : ""
            )}
          >
            {isArabic ? (
              <ChevronLeftIcon className="w-5 h-5" />
            ) : (
              <ChevronRightIcon className="w-5 h-5" />
            )}
          </Link>
        </ul>
      </div>
    </div>
  );
};

export default Pagination;
